import { useCallback, useEffect, useState } from 'react';
import { BUILD_STAMP } from './lib/api';
import {
  ApiFailure,
  api,
  type DailyLogFeed as LogFeed,
  type PillarCode,
  type ProcurementFeed as Procurement,
  type Project,
  type SessionUser,
} from './lib/api';
import { Login } from './components/Login';
import { PILLAR_LEGAL_NAMES, PillarSwitcher } from './components/PillarSwitcher';
import { ProjectTable } from './components/ProjectTable';
import { NewProjectDialog } from './components/NewProjectDialog';
import {
  NavMenuButton,
  NavigationDrawer,
  NavigationRail,
} from './components/NavigationRail';
import { HoldPanel } from './components/HoldPanel';
import { WeatherPanel } from './components/WeatherPanel';
import { HealthBadge } from './components/HealthBadge';
import { ContactsDirectory } from './components/ContactsDirectory';
import { ContactDetailPanel } from './components/ContactDetail';
import { ReviewQueue } from './components/ReviewQueue';
import { MergeHistory } from './components/MergeHistory';
import { Dashboard, usePillarButtons } from './components/Dashboard';
import { CompanyWorld } from './components/CompanyWorld';
import { DailyLogFeed } from './components/DailyLogFeed';
import { ProcurementTable } from './components/ProcurementTable';
import { SchedulePanel } from './components/SchedulePanel';
import { DocumentLibrary } from './components/DocumentLibrary';
import { PhotoCapture } from './components/PhotoCapture';
import { NotificationCentre } from './components/NotificationCentre';
import { resyncIfEnabled } from './lib/push';
import { navigate, useRoute } from './lib/route';

type Phase = 'checking' | 'anonymous' | 'authenticated';

/**
 * The application shell.
 *
 * §1.4 step 3 requires the pillar drill-down to live at `/#/pillar/{code}`,
 * which a tab held in component state cannot do: the entity a person is looking
 * at has to survive a refresh and be sendable to a colleague. So the location
 * carries the view, and P4-9's rail reads it rather than holding its own.
 * Phase 1 proved the spine — argon2id login, server-side
 * session, pillar scoping, the 403 boundary and the health probe. Phase 3 hangs
 * the §1.2 dashboard, the §1.3 pillar buttons and the §1.4 Company World off it.
 */
export default function App(): JSX.Element {
  const [phase, setPhase] = useState<Phase>('checking');
  const [user, setUser] = useState<SessionUser | null>(null);
  const [scope, setScope] = useState<PillarCode | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [notice, setNotice] = useState<string | null>(null);
  const [openContactId, setOpenContactId] = useState<string | null>(null);
  const [creatingProject, setCreatingProject] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const route = useRoute();
  const pillars = usePillarButtons(phase === 'authenticated');

  // Rehydrate from the session cookie so a refresh does not log the user out.
  useEffect(() => {
    // Printed so a reviewer can confirm which build their browser is running.
    console.info(`CALM Ops build ${BUILD_STAMP}`);
    void (async () => {
      try {
        const { user: current, entityScope } = await api.me();
        setUser(current);
        setScope(entityScope);
        setPhase('authenticated');
      } catch {
        setPhase('anonymous');
      }
    })();
  }, []);

  const loadProjects = useCallback(async () => {
    try {
      const { projects: rows } = await api.projects();
      setProjects(rows);
      setNotice(null);
    } catch (failure) {
      setProjects([]);
      if (failure instanceof ApiFailure && failure.status === 403) {
        // The scope denial the API returns, surfaced verbatim in spirit: the
        // user is told what they may see, never what they may not.
        setNotice(
          `Your account is scoped to ${(failure.body.grantedPillars ?? []).join(', ') || 'no entities'}.`,
        );
      } else {
        setNotice('Projects could not be loaded.');
      }
    }
  }, []);

  useEffect(() => {
    if (phase === 'authenticated') void loadProjects();
  }, [phase, scope, loadProjects]);


  const changeScope = useCallback(async (next: PillarCode | null): Promise<void> => {
    try {
      const { entityScope } = await api.setScope(next);
      setScope(entityScope);
    } catch (failure) {
      // P4-2. A bare catch here told every failure the same story: that the
      // user lacked permission. A 400 invalid_entity is a malformed request —
      // our bug, not their access — and saying "not available to your account"
      // sends someone to an administrator to fix something an administrator
      // cannot fix. Only a real scope denial gets the permission wording.
      if (failure instanceof ApiFailure && failure.status === 403) {
        setNotice('That entity scope is not available to your account.');
      } else if (failure instanceof ApiFailure && failure.status === 400) {
        setNotice('That entity could not be recognised. Reload and try again.');
      } else {
        setNotice('The entity scope could not be changed.');
      }
    }
  }, []);

  // A `/#/pillar/{code}` link is a shareable address, so arriving on one by
  // paste or refresh has to put the session in the same entity scope that
  // clicking the button would have. Without this the chrome would say CLM
  // while the session was still unscoped.
  useEffect(() => {
    if (phase !== 'authenticated') return;
    if (route.name !== 'pillar') return;
    if (scope === route.code) return;
    if (!user?.pillars.includes(route.code as PillarCode)) return;
    void changeScope(route.code as PillarCode);
  }, [phase, route, scope, user, changeScope]);

  /**
   * Repair a device that believes it is subscribed while the server has no
   * endpoint for it — a database restore, a revoked row, or a
   * `pushsubscriptionchange` the worker missed while the browser was closed.
   * That mismatch is invisible from both ends and never heals on its own, so
   * an already-permitted device re-posts its subscription once per session.
   * This never prompts: `resyncIfEnabled` returns immediately unless
   * permission was already granted.
   */
  useEffect(() => {
    if (phase !== 'authenticated') return;
    void resyncIfEnabled();
  }, [phase]);

  /**
   * §1.4 steps 1-4: setting the scope on the server, re-skinning the chrome,
   * navigating to `/#/pillar/{code}` and persisting the scope for the session
   * are one action, in that order. The navigation happens after the server has
   * accepted the scope, so a refused scope never leaves a person on a page
   * their session cannot load.
   */
  async function openPillar(code: string): Promise<void> {
    await changeScope(code as PillarCode);
    navigate(`/pillar/${code}`);
  }

  async function signOut(): Promise<void> {
    await api.logout();
    setUser(null);
    setProjects([]);
    setScope(null);
    setPhase('anonymous');
    navigate('/');
  }

  if (phase === 'checking') {
    return (
      <main className="grid min-h-screen place-items-center bg-surface">
        <p className="text-body text-text-secondary">Loading…</p>
      </main>
    );
  }

  if (phase === 'anonymous' || !user) {
    return (
      <Login
        onAuthenticated={(next, entityScope) => {
          setUser(next);
          setScope(entityScope);
          setPhase('authenticated');
        }}
      />
    );
  }

  // §0.4: chrome carries the pillar identity; the content area stays neutral
  // so that data is never tinted by whose screen it is on.
  const chromePillar = scope ?? user.pillars[0] ?? 'CAC';
  const activePillarCode = route.name === 'pillar' ? route.code : null;

  const entityLine = scope ? PILLAR_LEGAL_NAMES[chromePillar] : 'All granted entities';

  return (
    // The rail is fixed, so the shell is inset by its width from `lg` up. Below
    // that the rail is off-canvas and the shell uses the full width.
    <div className="min-h-screen bg-surface lg:pl-[var(--nav-rail-width)]">
      <NavigationRail route={route} pillar={chromePillar} entityLine={entityLine} />
      <NavigationDrawer
        open={navOpen}
        onClose={() => setNavOpen(false)}
        onNavigate={() => setNavOpen(false)}
        route={route}
        pillar={chromePillar}
        entityLine={entityLine}
      />

      <header
        className="pillar-header flex flex-wrap items-center justify-between gap-3 px-4 py-3 lg:px-6"
        data-pillar={chromePillar}
      >
        <div className="flex items-center gap-3">
          <NavMenuButton onOpen={() => setNavOpen(true)} />
          {/* The wordmark and the entity line both live at the top of the rail
              from `lg` up; repeating them in the header there would print the
              same two lines twice on one screen. */}
          <div className="lg:hidden">
            <p className="micro-label text-white/70">{entityLine}</p>
            <p className="wordmark text-h2 font-semibold">CALM Ops</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-body text-white/85">
            {user.fullName} · <span className="micro-label">{user.role}</span>
          </span>
          {/* The bell sits in the header rather than the rail: it changes while
              you are on some other screen, so it has to be visible from all of
              them, and the navy chrome is the only surface §0.1 lets its gold
              unread count live on. */}
          <NotificationCentre />
          <button
            type="button"
            onClick={signOut}
            className="h-9 rounded-card border border-white/40 px-3 text-micro font-medium uppercase text-white hover:bg-white/10"
          >
            Sign out
          </button>
        </div>
      </header>

      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border bg-white px-4 py-2 lg:px-6">
        <PillarSwitcher granted={user.pillars} active={scope} onChange={changeScope} />
        <HealthBadge />
      </div>

      <main id="main-pane" className="px-4 py-5 lg:px-6">
        {notice ? (
          <p role="status" className="mb-4 text-body text-warning">
            {notice}
          </p>
        ) : null}

        {route.name === 'dashboard' ? (
          <Dashboard
            scope={scope}
            projects={projects}
            pillars={pillars}
            onOpenPillar={(code) => void openPillar(code)}
          />
        ) : null}

        {route.name === 'pillar' ? (
          <CompanyWorld
            code={activePillarCode!}
            identity={pillars.find((p) => p.code === activePillarCode)}
          />
        ) : null}

        {route.name === 'projects' || route.name === 'project' ? (
          <>
            <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
              <div>
                <h1 className="mb-1 text-h1 font-semibold text-text-primary">Projects</h1>
                <p className="text-body text-text-secondary">
                  {projects.length} project{projects.length === 1 ? '' : 's'} in scope
                  {scope ? ` · ${scope}` : ''}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setCreatingProject(true)}
                className="rounded bg-royal px-4 py-2 text-body font-semibold text-white hover:bg-navy"
              >
                New project
              </button>
            </div>
            {/* One project selected: its §4.5 hold and its P4-8 site weather
                sit above the list it came from, so the gates and the row stay
                on one screen. */}
            {route.name === 'project' ? <HoldPanel projectId={route.id} /> : null}
            {route.name === 'project' ? <WeatherPanel projectId={route.id} /> : null}
            <ProjectTable projects={projects} />
          </>
        ) : null}

        {route.name === 'logs' ? <DailyLogScreen scope={scope} /> : null}
        {route.name === 'documents' ? <DocumentLibrary scope={scope} /> : null}
        {route.name === 'photos' ? <PhotoCapture scope={scope} /> : null}
        {route.name === 'procurement' ? <ProcurementScreen scope={scope} /> : null}
        {route.name === 'schedule' ? <SchedulePanel /> : null}

        {route.name === 'contacts' ? (
          <ContactsDirectory onOpenContact={(contact) => setOpenContactId(contact.id)} />
        ) : null}
        {route.name === 'review' ? <ReviewQueue /> : null}
        {route.name === 'merges' ? <MergeHistory /> : null}
      </main>

      {/* A newly created project should be visible in the list behind the
          dialog the moment it closes, so the list is re-read rather than
          patched locally — the server decides what is in scope, not the form. */}
      {creatingProject ? (
        <NewProjectDialog
          pillars={user.pillars}
          entityScope={scope}
          onClose={() => setCreatingProject(false)}
          onCreated={(created) => {
            setCreatingProject(false);
            void loadProjects();
            navigate(`#/projects/${created.id}`);
          }}
        />
      ) : null}

      {openContactId ? (
        <ContactDetailPanel id={openContactId} onClose={() => setOpenContactId(null)} />
      ) : null}
    </div>
  );
}

/** Deliverable 16: the cross-project daily-log feed on its own screen. */
function DailyLogScreen({ scope }: { scope: PillarCode | null }): JSX.Element {
  const [feed, setFeed] = useState<LogFeed | null>(null);
  const [days, setDays] = useState(14);

  useEffect(() => {
    let live = true;
    setFeed(null);
    void (async () => {
      try {
        const next = await api.dailyLogs(scope, days);
        if (live) setFeed(next);
      } catch {
        if (live) setFeed({ logs: [], days, total: 0 });
      }
    })();
    return () => {
      live = false;
    };
  }, [scope, days]);

  return (
    <>
      <div className="mb-4 flex flex-wrap items-end justify-between gap-2">
        <div>
          <h1 className="text-h1 font-semibold text-text-primary">Daily logs</h1>
          <p className="text-body text-text-secondary">
            Reverse-chronological across every project in scope
            {scope ? ` · ${scope}` : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-micro text-text-secondary" htmlFor="log-window">
            Window
          </label>
          <select
            id="log-window"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="min-h-touch rounded-card border border-border bg-white px-2 text-body"
          >
            <option value={7}>7 days</option>
            <option value={14}>14 days</option>
            <option value={30}>30 days</option>
            <option value={90}>90 days</option>
          </select>
        </div>
      </div>
      {feed === null ? (
        <div className="h-24 animate-pulse rounded-card bg-white" />
      ) : (
        <DailyLogFeed logs={feed.logs} days={feed.days} />
      )}
    </>
  );
}

/** Deliverable 16: procurement across the scope, ordered by the order-by date. */
function ProcurementScreen({ scope }: { scope: PillarCode | null }): JSX.Element {
  const [feed, setFeed] = useState<Procurement | null>(null);

  useEffect(() => {
    let live = true;
    setFeed(null);
    void (async () => {
      try {
        const next = await api.procurement(scope);
        if (live) setFeed(next);
      } catch {
        if (live) setFeed({ items: [], atRisk: 0, total: 0 });
      }
    })();
    return () => {
      live = false;
    };
  }, [scope]);

  return (
    <>
      <h1 className="mb-1 text-h1 font-semibold text-text-primary">Procurement</h1>
      <p className="mb-4 text-body text-text-secondary">
        Every item in scope, soonest order-by date first
        {scope ? ` · ${scope}` : ''}
      </p>
      {feed === null ? (
        <div className="h-24 animate-pulse rounded-card bg-white" />
      ) : (
        <ProcurementTable items={feed.items} atRisk={feed.atRisk} />
      )}
    </>
  );
}
